import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { StatusBadge } from "../../components/etams/StatusBadge";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";
import { useDaftar } from "../../hooks/useDaftar";
import { useAsetMeta, label, tone } from "../../hooks/useAsetMeta";
import { listAset, listAsetTanpaJadwal, apiErrorMessage } from "../../lib/asetApi";
import { ASET_KELOLA_ROLES } from "../../lib/roles";
import { Wrench, Plus, RefreshCw, CalendarX2 } from "lucide-react";
import { toast } from "sonner";
import AsetFormDialog from "./AsetFormDialog";
import { useRegions } from "./bantuAset";

// Daftar Aset (L3a, 4b-1) — per cabang. OWNER menyaring region; KC/ADMIN
// dijepit region profil oleh backend (`_lingkup_baca`). Filter status/kategori
// ke server; cari/urut/paginasi di klien (himpunan aset satu cabang terbatas
// secara struktur — pola Holidays). `?tanpa_jadwal=1` = kartu dashboard
// "Aset tanpa jadwal maintenance" → GET /api/aset/tanpa-jadwal.
//
// L4/L6: label + rona dari GET /api/aset/meta — nol label ditulis di sini.
// Tombol tulis cermin backend: buat = ASET_KELOLA_ROLES (OWNER, KEPALA_CABANG).

const SEMUA = "__semua__";

export default function DaftarAset() {
  const { roleCode, isOwner, profile } = useAuth();
  const canKelola = ASET_KELOLA_ROLES.includes(roleCode);
  const { meta } = useAsetMeta();
  const [params, setParams] = useSearchParams();
  const tanpaJadwal = params.get("tanpa_jadwal") === "1";

  const [items, setItems] = useState([]);
  const [memuat, setMemuat] = useState(true);
  const [regionFilter, setRegionFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [kategoriFilter, setKategoriFilter] = useState("");
  const [formTerbuka, setFormTerbuka] = useState(false);
  const regions = useRegions(isOwner);

  const muat = useCallback(async () => {
    setMemuat(true);
    try {
      const p = {};
      if (isOwner && regionFilter) p.region_id = regionFilter;
      let r;
      if (tanpaJadwal) {
        r = await listAsetTanpaJadwal(p);
      } else {
        if (statusFilter) p.status = statusFilter;
        if (kategoriFilter) p.kategori = kategoriFilter;
        r = await listAset(p);
      }
      let baris = r.data?.items || [];
      // /tanpa-jadwal nol menerima kategori — saring di klien.
      if (tanpaJadwal && kategoriFilter) baris = baris.filter((a) => a.kategori === kategoriFilter);
      setItems(baris);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat daftar aset."));
      setItems([]);
    } finally {
      setMemuat(false);
    }
  }, [isOwner, regionFilter, statusFilter, kategoriFilter, tanpaJadwal]);

  useEffect(() => { muat(); }, [muat]);

  const KOLOM = [
    { kunci: "kode", judul: "Kode" },
    { kunci: "nama", judul: "Nama" },
    { kunci: "kategori", judul: "Kategori", ambil: (a) => label(meta, "kategori", a.kategori) },
    { kunci: "region", judul: "Wilayah", ambil: (a) => a.region?.name || "", nilai: (a) => a.region?.name || "" },
    { kunci: "store", judul: "Cabang", ambil: (a) => a.store?.name || "", nilai: (a) => a.store?.name || "" },
    { kunci: "pic", judul: "PIC", ambil: (a) => a.pic?.full_name || "", nilai: (a) => a.pic?.full_name || "" },
    { kunci: "status", judul: "Status", ambil: (a) => label(meta, "status_aset", a.status) },
  ];
  const daftar = useDaftar(items, {
    kunciCari: ["kode", "nama", "jenis", "nomor_seri", (a) => a.pic?.full_name, (a) => a.region?.name, (a) => a.store?.name],
    kolom: KOLOM,
    urutAwal: { kunci: "kode", arah: "asc" },
  });

  const keluarTanpaJadwal = () => { params.delete("tanpa_jadwal"); setParams(params, { replace: true }); };

  return (
    <div className="space-y-6" data-testid="daftar-aset-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Wrench className="h-6 w-6" /> Daftar Aset
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Mesin, kendaraan, dan alat kerja cabang beserta jadwal maintenance-nya.
            {!isOwner && profile?.region?.name ? ` Wilayah: ${profile.region.name}.` : ""}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" asChild data-testid="aset-ke-inventaris">
            <Link to="/aset/inventaris">Inventaris operasional</Link>
          </Button>
          <Button variant="outline" size="sm" onClick={muat} data-testid="aset-refresh">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          {canKelola && (
            <Button size="sm" onClick={() => setFormTerbuka(true)} data-testid="aset-create-btn">
              <Plus className="h-4 w-4 mr-1" /> Aset Baru
            </Button>
          )}
        </div>
      </div>

      {tanpaJadwal && (
        <div className="px-3 py-2 rounded-md bg-warning-soft text-warning-soft-foreground text-sm flex items-center justify-between gap-2 flex-wrap"
             data-testid="aset-tanpa-jadwal-banner">
          <span className="flex items-center gap-2"><CalendarX2 size={16} /> Menampilkan aset aktif yang belum punya jadwal maintenance.</span>
          <Button variant="outline" size="sm" onClick={keluarTanpaJadwal} data-testid="aset-tanpa-jadwal-keluar">Tampilkan semua aset</Button>
        </div>
      )}

      <Card className="p-4">
        <PalangDaftar daftar={daftar} placeholder="Cari kode / nama / nomor seri / PIC…" testId="aset-palang"
                      ekspor={{ nama: tanpaJadwal ? "aset_tanpa_jadwal" : "daftar_aset", kolom: KOLOM }}>
          {isOwner && regions.length > 0 && (
            <Select value={regionFilter || SEMUA} onValueChange={(v) => setRegionFilter(v === SEMUA ? "" : v)}>
              <SelectTrigger className="w-48 h-9" data-testid="aset-filter-region"><SelectValue placeholder="Wilayah" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={SEMUA}>Semua Wilayah</SelectItem>
                {regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={kategoriFilter || SEMUA} onValueChange={(v) => setKategoriFilter(v === SEMUA ? "" : v)}>
            <SelectTrigger className="w-44 h-9" data-testid="aset-filter-kategori"><SelectValue placeholder="Kategori" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={SEMUA}>Semua Kategori</SelectItem>
              {(meta?.kategori || []).map((k) => <SelectItem key={k.code} value={k.code}>{k.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {!tanpaJadwal && (
            <Select value={statusFilter || SEMUA} onValueChange={(v) => setStatusFilter(v === SEMUA ? "" : v)}>
              <SelectTrigger className="w-44 h-9" data-testid="aset-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={SEMUA}>Semua Status</SelectItem>
                {(meta?.status_aset || []).map((s) => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </PalangDaftar>

        {memuat ? (
          <div className="py-8 text-center text-sm text-muted-foreground" data-testid="aset-memuat">Memuat...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                <tr>
                  <ThUrut daftar={daftar} kunci="kode" className="text-left px-3 py-2">Kode</ThUrut>
                  <ThUrut daftar={daftar} kunci="nama" className="text-left px-3 py-2">Nama</ThUrut>
                  <ThUrut daftar={daftar} kunci="kategori" className="text-left px-3 py-2">Kategori</ThUrut>
                  {isOwner && <ThUrut daftar={daftar} kunci="region" className="text-left px-3 py-2">Wilayah</ThUrut>}
                  <ThUrut daftar={daftar} kunci="store" className="text-left px-3 py-2">Cabang</ThUrut>
                  <ThUrut daftar={daftar} kunci="pic" className="text-left px-3 py-2">PIC</ThUrut>
                  <ThUrut daftar={daftar} kunci="status" className="text-left px-3 py-2">Status</ThUrut>
                </tr>
              </thead>
              <tbody>
                {daftar.total === 0 && (
                  <tr><td colSpan={isOwner ? 7 : 6} className="px-3 py-8 text-center text-muted-foreground" data-testid="aset-kosong">
                    {daftar.cari.trim()
                      ? "Tidak ada aset yang cocok."
                      : tanpaJadwal
                        ? "Semua aset aktif sudah punya jadwal maintenance."
                        : statusFilter || kategoriFilter || regionFilter
                          ? "Tidak ada aset yang cocok dengan filter ini."
                          : canKelola
                            ? "Belum ada aset terdaftar. Tekan \"Aset Baru\" untuk mulai memasukkan aset cabang."
                            : "Belum ada aset terdaftar. Kepala Cabang yang memasukkan aset cabang."}
                  </td></tr>
                )}
                {daftar.tampil.map((a) => (
                  <tr key={a.id} className="border-t border-border hover:bg-muted/50" data-testid={`aset-row-${a.id}`}>
                    <td className="px-3 py-2 font-mono text-xs">
                      <Link to={`/aset/${a.id}`} className="underline underline-offset-2 hover:text-foreground" data-testid={`aset-link-${a.id}`}>{a.kode}</Link>
                    </td>
                    <td className="px-3 py-2">
                      <Link to={`/aset/${a.id}`} className="font-medium hover:underline">{a.nama}</Link>
                      {a.jenis ? <span className="block text-xs text-muted-foreground">{a.jenis}{a.nomor_seri ? ` · ${a.nomor_seri}` : ""}</span> : null}
                    </td>
                    <td className="px-3 py-2">{label(meta, "kategori", a.kategori)}</td>
                    {isOwner && <td className="px-3 py-2 text-xs">{a.region?.name || "—"}</td>}
                    <td className="px-3 py-2 text-xs">{a.store?.name || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-3 py-2 text-xs">{a.pic?.full_name || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-3 py-2">
                      <StatusBadge tone={tone(meta, "status_aset", a.status)} testId={`aset-status-${a.id}`}>
                        {label(meta, "status_aset", a.status)}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Paginasi daftar={daftar} testId="aset-paginasi" />
          </div>
        )}
      </Card>

      <AsetFormDialog
        terbuka={formTerbuka}
        onTutup={() => setFormTerbuka(false)}
        onTersimpan={() => { setFormTerbuka(false); muat(); }}
        meta={meta}
        aset={null}
        isOwner={isOwner}
        regionProfil={profile?.region_id || null}
        regionNama={profile?.region?.name || null}
      />
    </div>
  );
}
